<div class="row overide-header text-end m-auto" [formGroup]="sectionForm">
  <div class="tkts-popup-body">

    <!-- Comments List Area -->
    <div class="notes-wrap" id="noteWrap" #noteWrap>

      <!-- Loading Skeleton - Modern shimmer effect -->
      @if (isLoadingComments) {
        @for (i of [1, 2, 3]; track i) {
          <div class="comment-skeleton">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-content">
              <div class="skeleton-line short"></div>
              <div class="skeleton-line medium"></div>
              <div class="skeleton-line long"></div>
            </div>
          </div>
        }
      }

      <!-- Loop through comments - WHITE CARD DESIGN -->
      @if (!isLoadingComments) {
        @for (comment of comments; track comment; let i = $index) {
          <div class="comment-card">
            <!-- Unified Header Bar (Avatar + Name + Timestamp + Actions) -->
            <div class="comment-header">
              <div class="header-left">
                <!-- User Avatar -->
                <div class="user-avatar" [style.background-image]="'url(' + getUserAvatar(comment.creator_id) + ')'"
                  attr.aria-label="{{ getUserFullName(comment.creator_id) }}">
                  <span class="avatar-fallback">{{ getUserInitials(comment.creator_id) }}</span>
                </div>
                <!-- User Meta (Name + Timestamp - Inline) -->
                <div class="user-meta">
                  <span class="user-name">{{ getUserFullName(comment.creator_id) }}</span>
                  <span class="meta-separator">•</span>
                  <span class="timestamp" [title]="formatTimestamp(comment.created_at)"
                    attr.aria-label="Posted {{ formatTimestamp(comment.created_at) }}">
                    {{ getRelativeTime(comment.created_at) }}
                  </span>
                </div>
              </div>
              <!-- Header Right (Compact Action Buttons) -->
              <div class="header-right">
                <button class="header-action-btn reply-btn" type="button" (click)="replyToComment(comment.id)"
                  attr.aria-label="Reply to comment" title="Reply">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 14 4 9 9 4"></polyline>
                    <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
                  </svg>
                </button>
                <button class="header-action-btn react-btn" type="button"
                  (click)="toggleReactionPicker(comment.id, 'header')" attr.aria-label="React to comment" title="React">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                    <line x1="9" y1="9" x2="9.01" y2="9"></line>
                    <line x1="15" y1="9" x2="15.01" y2="9"></line>
                  </svg>
                </button>
                <!-- Header-triggered Reaction Picker -->
                @if (isReactionPickerOpen(comment.id, 'header')) {
                  <div class="reaction-picker">
                    @for (emoji of quickReactions; track emoji) {
                      <button type="button" class="picker-emoji"
                        (click)="toggleReaction(comment.id, emoji)" [title]="emoji" attr.aria-label="React with {{ emoji }}">
                        {{ emoji }}
                      </button>
                    }
                  </div>
                }
                <button class="header-action-btn delete-btn" type="button" (click)="deleteComment(comment.id)"
                  attr.aria-label="Delete comment" title="Delete">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
            <!-- Comment Body -->
            <div class="comment-body">
              <!-- Comment Text -->
              <p class="comment-text">{{ comment.comment }}</p>
              <!-- Attachments (if any) -->
              @if (comment.attach_files && comment.attach_files.length > 0) {
                <div class="comment-attachments">
                  <!-- Image Attachment: Show thumbnail -->
                  @for (file of comment.attach_files; track file) {
                    <div>
                      @if (isImageFile(file)) {
                        <div class="attachment-item image-attachment"
                          (click)="openImagePreview(file, $event)" attr.aria-label="Preview image">
                          <img [src]="file.url" [alt]="file.original_name" class="attachment-thumbnail"
                            loading="lazy" (error)="handleImageError($event)">
                          <span matTooltip="{{ file.original_name }}">{{ file.original_name }}</span>
                        </div>
                      }
                      <!-- Non-Image Attachment: Show SVG icon -->
                      @if (!isImageFile(file)) {
                        <a [href]="file.url" class="attachment-item" target="_blank"
                          attr.aria-label="Download attachment">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path
                              d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48">
                            </path>
                          </svg>
                          <span matTooltip="{{ file.original_name || 'Attachment' }}">{{ file.original_name || 'Attachment' }}</span>
                        </a>
                      }
                    </div>
                  }
                </div>
              }
            </div>
            <!-- Inline Emoji Reactions (Slack/GitHub/Linear style) -->
            @if (comment.reactions && comment.reactions.length > 0) {
              <div class="comment-reactions">
                @for (reaction of comment.reactions; track reaction) {
                  <button type="button" class="reaction-bubble"
                    [class.active]="userHasReacted(reaction)" (click)="toggleReaction(comment.id, reaction.emoji)"
                    [title]="getReactionTooltip(reaction)"
                    attr.aria-label="{{ reaction.count }} {{ reaction.emoji }} reaction{{ reaction.count > 1 ? 's' : '' }}">
                    <span class="reaction-emoji">{{ reaction.emoji }}</span>
                    <span class="reaction-count">{{ reaction.count }}</span>
                  </button>
                }
                <!-- Add Reaction Button (appears on hover) -->
                <button type="button" class="add-reaction-btn" (click)="toggleReactionPicker(comment.id, 'inline')"
                  title="Add reaction" attr.aria-label="Add reaction">
                  ➕
                </button>
                <!-- Quick Reaction Picker (hover popup) -->
                @if (isReactionPickerOpen(comment.id, 'inline')) {
                  <div class="reaction-picker">
                    @for (emoji of quickReactions; track emoji) {
                      <button type="button" class="picker-emoji"
                        (click)="toggleReaction(comment.id, emoji)" [title]="emoji" attr.aria-label="React with {{ emoji }}">
                        {{ emoji }}
                      </button>
                    }
                  </div>
                }
              </div>
            }
            <!-- Show reaction picker trigger if no reactions yet -->
            @if (!comment.reactions || comment.reactions.length === 0) {
              <div class="comment-reactions">
                <button type="button" class="add-reaction-btn initial" (click)="toggleReactionPicker(comment.id, 'inline')"
                  title="Add reaction" attr.aria-label="Add reaction">
                  😊 Add Reaction
                </button>
                <!-- Quick Reaction Picker -->
                @if (isReactionPickerOpen(comment.id, 'inline')) {
                  <div class="reaction-picker">
                    @for (emoji of quickReactions; track emoji) {
                      <button type="button" class="picker-emoji"
                        (click)="toggleReaction(comment.id, emoji)" [title]="emoji" attr.aria-label="React with {{ emoji }}">
                        {{ emoji }}
                      </button>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      }

      <!-- Empty State - ENHANCED -->
      @if (!isLoadingComments && comments.length === 0) {
        <div class="comments-empty-state">
          <div class="empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none"
              stroke="#9ca3af" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
          <p class="empty-title">No comments yet</p>
          <p class="empty-subtitle">Start the conversation by adding the first comment</p>
          <button type="button" class="empty-action-btn" (click)="focusCommentInput()" attr.aria-label="Add comment">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>Add Comment</span>
          </button>
        </div>
      }

    </div>

    <!-- Comment Input Area -->
    <div class="textarea-tkt">
      <div class="row mb-4">

        <!-- User Avatar -->
        <div class="col-md-1">
          <app-profile-avatar [fullName]="commentAvatarFullName" [profileUrl]="commentAvatarUrl" [widthSize]="'36px'"
            [heightSize]="'36px'" [userStatusSlug]="commentAvatarStatusSlug">
          </app-profile-avatar>
        </div>

        <!-- Textarea and Toolbar -->
        <div class="col-md-11">
          <mat-form-field class="example-full-width1">
            <textarea matInput formControlName="comment" placeholder="Write a comment..." rows="3"
              (keydown)="handleKeydown($event)" attr.aria-label="Comment input">
            </textarea>
          </mat-form-field>

          <!-- Rich Toolbar (5 buttons: attach, image, emoji, @mention, submit) -->
          <div class="comment-toolbar">
            <!-- Attach File Button -->
            <button type="button" class="toolbar-btn attach-btn" (click)="toggleAttachmentUpload()"
              attr.aria-label="Attach file" title="Attach file">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path
                  d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48">
                </path>
              </svg>
            </button>

            <!-- Upload Image Button -->
            <button type="button" class="toolbar-btn image-btn" (click)="toggleImageUpload()"
              attr.aria-label="Upload image" title="Upload image">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </button>

            <!-- Emoji Picker Button -->
            <button type="button" class="toolbar-btn emoji-btn" (click)="toggleEmojiPicker()"
              attr.aria-label="Insert emoji" title="Insert emoji">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                <line x1="9" y1="9" x2="9.01" y2="9"></line>
                <line x1="15" y1="9" x2="15.01" y2="9"></line>
              </svg>
            </button>

            <!-- Mention User Button -->
            <button type="button" class="toolbar-btn mention-btn" (click)="insertMention()"
              attr.aria-label="Mention user" title="@Mention user">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M16 12v-1a4 4 0 1 0-8 0v1"></path>
                <line x1="12" y1="12" x2="12" y2="16"></line>
              </svg>
            </button>

            <!-- Spacer (push submit to right) -->
            <div class="toolbar-spacer"></div>

            <!-- Submit Button -->
            <button type="button" class="toolbar-btn submit-btn" (click)="addComment()"
              [disabled]="sectionForm.invalid || isSubmittingComment" attr.aria-label="Submit comment"
              title="Submit comment">
              <!-- Spinner (shown when submitting) -->
              @if (isSubmittingComment) {
                <svg class="spinner" xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
                  <path d="M12 2 A10 10 0 0 1 22 12" stroke-dasharray="15.7" stroke-dashoffset="15.7">
                    <animate attributeName="stroke-dashoffset" values="15.7;0;15.7" dur="1s" repeatCount="indefinite" />
                  </path>
                </svg>
              }
              <!-- Submit Icon (shown when not submitting) -->
              @if (!isSubmittingComment) {
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              }
            </button>
          </div>

          <!-- Emoji Picker Popup (conditional) -->
          @if (showEmojiPicker) {
            <div class="emoji-picker">
              <div class="emoji-grid">
                @for (emoji of commonEmojis; track emoji) {
                  <button type="button" (click)="insertEmoji(emoji)" class="emoji-item"
                    attr.aria-label="Insert {{ emoji }}">
                    {{ emoji }}
                  </button>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- File Attachment Component (Conditional) -->
    @if (allowUploadFile) {
      <div class="attachment-container-comments">
        <app-attachment [fileTypes]="allowedFileTypes" [multipleUpload]="multipleUpload"
          [multipleUploadData]="attachmentsArr" [module_id]="module_id" (callback)="attachment($event)">
        </app-attachment>
      </div>
    }

  </div>
</div>
